import { useState, useEffect, useMemo } from 'react';
import { buildDisplayLogic } from '../../../tester/pages/survey/helpers';

/**
 * This hook is used to determine if the current page is the last page of the survey.
 *
 * @param {boolean} isLastPage - Initial state indicating if the current page is the last page.
 * @param {Array<Object>} allQuestions - List of all questions in the survey.
 * @param {Array<Object>} accumulatedAnswers - List of accumulated answers so far.
 * @param {Array<Object>} pagesList - List of all pages in the survey.
 * @param {Object} page - The current page object.
 * @param {Array<Object>} treatedAccumulatedAnswers - List of treated accumulated answers.
 * @returns {boolean} isLastPageState - State indicating if the current page is the last page.
 */
export const useLastPage = (isLastPage, allQuestions, accumulatedAnswers, pagesList, page, treatedAccumulatedAnswers) => {
  const [isLastPageState, setIsLastPageState] = useState(isLastPage);

  const currentPageAnswersT = useMemo(() => allQuestions.filter((q) => q.answer !== undefined)
    .map((q) => {
      const { id, answer, questionType } = q;
      const answerResponse = questionType === 'range' ? answer : Object.keys(answer)[0];
      const answerResponseQuestionMultiple = questionType === 'multiple_select' ? { questionOptionIds: Object.keys(answer) }
        : { questionOptionId: answerResponse };
      return {
        questionId: id,
        ...answerResponseQuestionMultiple,
      };
    }), [allQuestions]);

  useEffect(() => {
    const calculateIsLastPage = () => {
      // Combine accumulated answers with current page answers
      const allAnswersSoFar = treatedAccumulatedAnswers.length > 0
        ? [...treatedAccumulatedAnswers, ...currentPageAnswersT]
        : [...currentPageAnswersT];

      // Check if there are any future pages with visible questions
      const currentPageIndex = pagesList.findIndex((p) => p.id === page.id);
      let hasFutureVisiblePages = false;
      for (let i = currentPageIndex + 1; i < pagesList.length; i += 1) {
        const futurePage = pagesList[i];
        const futureQuestions = futurePage.questions || [];
        const futureQuestionVisibility = buildDisplayLogic(allAnswersSoFar, pagesList);

        const visibleQuestions = futureQuestions.filter((q) => {
          const isVisible = futureQuestionVisibility[q.id] !== undefined ? futureQuestionVisibility[q.id] : true;
          return isVisible;
        });

        if (visibleQuestions.length > 0) {
          hasFutureVisiblePages = true;
          break;
        }
      }

      // Set isLastPage accordingly
      setIsLastPageState(!hasFutureVisiblePages);
    };

    calculateIsLastPage();
  }, [allQuestions, accumulatedAnswers, pagesList, page, treatedAccumulatedAnswers, currentPageAnswersT]);
  return isLastPageState;
};
